import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, ListFilter } from 'lucide-react';

import { DDSeg } from './DigestSummary';
import { toneFor, rowIdentityFor, rowNeedsContacts, isTerminal, statusLabelFor, rowStateFor, rowStateOptions, contactedLabelFor, byFreshThenBest } from './digestHelpers';

/**
 * The triage queue: a scannable list of everything the agent proposed today,
 * with the detail for the selected row shown alongside rather than in a modal.
 *
 * WHY A MASTER/DETAIL LIST AND NOT THE OLD CARD GRID. A digest is routinely 40
 * to 100 rows. Rendering each as a full card with its own draft meant the
 * reviewer scrolled past a wall of near-identical blocks and could not compare
 * two opportunities without losing both. A dense list makes the day surveyable
 * and keeps one detail pane in a fixed place.
 */

const PAGE = 20;

/**
 * The list keeps the agent's own score order — fit × recency × reachability, as
 * computed by the selector — with one exception: contacts already emailed or
 * invited sink below the fresh ones (byFreshThenBest in digestHelpers.js).
 *
 * The Company A-Z sort that used to share this control was dropped in favour of
 * filtering by status. Sorting rearranged a list the user still had to read in
 * full; filtering removes the rows they are not working on, which is what the
 * question "which ones failed?" actually asks for.
 */


const Checkbox = ({ checked, onChange, label }) => (
    <span
        role="checkbox"
        aria-checked={checked}
        aria-label={label}
        tabIndex={0}
        onClick={(e) => { e.stopPropagation(); onChange(); }}
        onKeyDown={(e) => {
            if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); onChange(); }
        }}
        style={{
            width: 16, height: 16, borderRadius: 5, flexShrink: 0, marginTop: 2, cursor: 'pointer',
            border: checked ? '0' : '1.5px solid var(--cc-border-strong)',
            background: checked ? 'var(--cc-primary)' : '#fff',
            display: 'grid', placeItems: 'center', color: '#fff',
        }}
    >
        {checked && <Check style={{ width: 11, height: 11 }} strokeWidth={3} />}
    </span>
);

const QueueRow = ({ action, selected, checked, onSelect, onToggleCheck }) => {
    const tone = toneFor(action.source?.type);
    const { title, meta } = rowIdentityFor(action);
    const needs = rowNeedsContacts(action);
    const settled = action.status !== 'proposed';

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => { if (e.key === 'Enter') onSelect(); }}
            style={{
                display: 'flex', gap: 10, padding: 12, borderRadius: 12, cursor: 'pointer', alignItems: 'flex-start',
                background: selected ? '#fff' : 'transparent',
                border: selected ? '1.5px solid var(--cc-primary)' : '1px solid transparent',
                boxShadow: selected ? '0 4px 14px rgba(63,87,224,.12)' : 'none',
                // A settled row has had its decision made; it stays readable but
                // stops competing with the rows still awaiting one.
                opacity: action.status === 'skipped' ? 0.55 : 1,
            }}
        >
            {/* Only rows a bulk action can change are selectable. A message
                row has no Approve or Skip, so a checkbox on it selected nothing. */}
            {action.kind === 'fetch_contact'
                ? <Checkbox checked={checked} onChange={onToggleCheck} label={`Select ${title}`} />
                : <span aria-hidden style={{ width: 18, flexShrink: 0 }} />}
            <div className="cc-grow" style={{ minWidth: 0 }}>
                <div className="cc-trunc" style={{ fontSize: 13, fontWeight: 700, color: 'var(--cc-ink-900)' }}>{title}</div>
                <div className="cc-trunc cc-tiny" style={{ marginTop: 1 }}>{meta}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    <span
                        className="cc-chip"
                        style={{ background: tone.bg, color: tone.fg, fontSize: 9.5, fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase' }}
                    >
                        {tone.label}
                    </span>
                    {settled ? (
                        <span
                            className="cc-tiny"
                            style={{
                                fontWeight: 600,
                                color: action.status === 'approved' ? '#047857'
                                    : action.status === 'failed' ? '#B91C1C'
                                        : isTerminal(action) ? 'var(--cc-primary)' : 'var(--cc-ink-500)',
                            }}
                        >
                            {statusLabelFor(action)}
                        </span>
                    ) : needs ? (
                        <>
                            <span className="cc-tiny" style={{ fontWeight: 600, color: 'var(--cc-primary)' }}>
                                {action.creditCost || 0} credits to find contacts
                            </span>
                            {action.ownedAtCompany > 0 && (
                                /* You may get fewer NEW people than you asked for.
                                   Not a price warning — the executor no longer
                                   charges for contacts you own — but a yield one. */
                                <span className="cc-chip cc-chip-neutral" style={{ fontSize: 9.5 }}>
                                    {action.ownedAtCompany} already yours
                                </span>
                            )}
                        </>
                    ) : (
                        /* rowStateFor, not a second opinion: it already folds in
                           "emailed on an earlier day", which the raw status does
                           not carry. Reading status alone showed "Draft ready"
                           next to a detail pane saying "Email sent · 2 Sep". */
                        <span
                            className="cc-tiny"
                            style={{ fontWeight: 600, color: contactedLabelFor(action) ? 'var(--cc-primary)' : '#047857' }}
                        >
                            {contactedLabelFor(action) || rowStateFor(action).label}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

const DigestQueue = ({
    actions, sourceCounts, sourceFilter, onSourceFilter,
    selectedId, onSelect, checkedIds, onToggleCheck, onToggleAllVisible,
}) => {
    const [statusFilter, setStatusFilter] = useState('all');
    const [menuOpen, setMenuOpen] = useState(false);
    const [limit, setLimit] = useState(PAGE);
    const menuRef = useRef(null);

    // Options come from the rows the SOURCE filter already left in view, so the
    // counts always describe the list actually on screen.
    const statusOptions = useMemo(() => rowStateOptions(actions), [actions]);

    /**
     * A filter that is no longer reachable must not keep filtering. Approving
     * the last failed row would otherwise leave "Failed" selected and the list
     * permanently empty, with no way back except reloading.
     */
    useEffect(() => {
        if (statusFilter !== 'all' && !statusOptions.some((o) => o.key === statusFilter)) {
            setStatusFilter('all');
        }
    }, [statusOptions, statusFilter]);

    useEffect(() => {
        if (!menuOpen) return undefined;
        const onDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [menuOpen]);

    const visible = useMemo(() => {
        const filtered = statusFilter === 'all'
            ? actions
            : actions.filter((a) => rowStateFor(a).key === statusFilter);
        return [...filtered].sort(byFreshThenBest);
    }, [actions, statusFilter]);

    const activeLabel = statusFilter === 'all'
        ? 'All statuses'
        : (statusOptions.find((o) => o.key === statusFilter)?.label || 'All statuses');

    const shown = visible.slice(0, limit);
    const selectable = shown.filter((a) => a.kind === 'fetch_contact');
    const allShownChecked = selectable.length > 0 && selectable.every((a) => checkedIds.has(a.actionId));

    /**
     * TWO GROUPS, NOT FOUR PEERS.
     *
     * Signals and Jobs are opportunities with nobody attached yet: acting on
     * one SPENDS CREDITS to find contacts, and the verb is Execute. A Contact
     * is a person already paid for: acting on one costs nothing and the verb is
     * Send. Listing all four in one segmented control implied they were the
     * same kind of thing and the same decision, which they are not.
     */
    const opportunityCount = (sourceCounts.signal || 0) + (sourceCounts.job || 0);
    const segItems = [
        { k: 'all', label: 'All', n: opportunityCount },
        { k: 'signal', label: 'Signals', n: sourceCounts.signal || 0 },
        { k: 'job', label: 'Jobs', n: sourceCounts.job || 0 },
    ].filter((it) => it.k === 'all' || it.n > 0);
    const contactCount = sourceCounts.contact || 0;

    return (
        <div className="cc-card" style={{ padding: '14px 12px', background: '#FAFBFD' }}>
            <div style={{ padding: '0 4px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, flexWrap: 'wrap' }}>
                    <div>
                        <div className="cc-eyebrow" style={{ marginBottom: 5 }}>Find contacts · costs credits</div>
                        <DDSeg
                            active={sourceFilter === 'contact' ? null : sourceFilter}
                            items={segItems}
                            onChange={(k) => { onSourceFilter(k); setLimit(PAGE); }}
                        />
                    </div>
                    {contactCount > 0 && (
                        <div>
                            <div className="cc-eyebrow" style={{ marginBottom: 5 }}>Reach out · free</div>
                            <button
                                type="button"
                                aria-pressed={sourceFilter === 'contact'}
                                className={`cc-toggle ${sourceFilter === 'contact' ? 'is-active' : ''}`}
                                onClick={() => { onSourceFilter('contact'); setLimit(PAGE); }}
                            >
                                Contacts<span className="cc-toggle-n">{contactCount}</span>
                            </button>
                        </div>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selectable.length > 0 && (
                        <>
                            <Checkbox
                                checked={allShownChecked}
                                onChange={() => onToggleAllVisible(selectable.map((a) => a.actionId), allShownChecked)}
                                label="Select all visible"
                            />
                            <span className="cc-tiny" style={{ fontWeight: 600 }}>
                                {allShownChecked ? 'Deselect visible' : 'Select all visible'}
                            </span>
                        </>
                    )}
                    <span style={{ flex: 1 }} />
                    {/*
                        Status filter, in the slot the sort control used to hold.
                        Its options are generated from the rows in view and read
                        exactly as the rows label themselves, so picking "Failed"
                        cannot show anything that says otherwise.
                    */}
                    <div ref={menuRef} style={{ position: 'relative' }}>
                        <button
                            type="button"
                            className="cc-btn cc-btn-ghost cc-btn-sm"
                            aria-haspopup="listbox"
                            aria-expanded={menuOpen}
                            onClick={() => setMenuOpen((o) => !o)}
                        >
                            <ListFilter style={{ width: 11, height: 11 }} />
                            {activeLabel}
                            {statusFilter !== 'all' && (
                                <span className="cc-toggle-n">{visible.length}</span>
                            )}
                            <ChevronDown style={{ width: 11, height: 11 }} />
                        </button>

                        {menuOpen && (
                            <div
                                role="listbox"
                                aria-label="Filter by status"
                                style={{
                                    position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 30,
                                    minWidth: 210, padding: 6, borderRadius: 12, background: '#fff',
                                    border: '1px solid var(--cc-border)',
                                    boxShadow: '0 12px 32px rgba(16,24,40,.14)',
                                    maxHeight: 320, overflowY: 'auto',
                                }}
                            >
                                {[{ key: 'all', label: 'All statuses', n: actions.length }, ...statusOptions].map((opt) => {
                                    const isActive = opt.key === statusFilter;
                                    return (
                                        <button
                                            key={opt.key}
                                            type="button"
                                            role="option"
                                            aria-selected={isActive}
                                            onClick={() => {
                                                setStatusFilter(opt.key);
                                                setLimit(PAGE);
                                                setMenuOpen(false);
                                            }}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                                                padding: '8px 10px', borderRadius: 8, border: 0, cursor: 'pointer',
                                                textAlign: 'left', fontSize: 12.5, fontWeight: 600,
                                                background: isActive ? 'var(--cc-primary-soft, #EEF1FE)' : 'transparent',
                                                color: isActive ? 'var(--cc-primary)' : 'var(--cc-ink-900)',
                                            }}
                                        >
                                            <span style={{ width: 12, display: 'grid', placeItems: 'center' }}>
                                                {isActive && <Check style={{ width: 11, height: 11 }} strokeWidth={3} />}
                                            </span>
                                            <span className="cc-grow cc-trunc">{opt.label}</span>
                                            <span className="cc-tiny" style={{ fontWeight: 700 }}>{opt.n}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {shown.length === 0 ? (
                <div style={{ padding: '28px 12px', textAlign: 'center' }}>
                    <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--cc-ink-900)' }}>Nothing in this view</p>
                    {/* Which of the two filters emptied the list is the only
                        useful thing to say here, and the status one is the only
                        one this control can undo. */}
                    {statusFilter === 'all' ? (
                        <p className="cc-tiny" style={{ marginTop: 4 }}>Today’s digest has no items from this source.</p>
                    ) : (
                        <>
                            <p className="cc-tiny" style={{ marginTop: 4 }}>
                                Nothing here is marked “{activeLabel}”.
                            </p>
                            <button
                                type="button"
                                className="cc-btn cc-btn-ghost cc-btn-sm"
                                style={{ marginTop: 10 }}
                                onClick={() => { setStatusFilter('all'); setLimit(PAGE); }}
                            >
                                Show all statuses
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {shown.map((a) => (
                        <QueueRow
                            key={a.actionId}
                            action={a}
                            selected={a.actionId === selectedId}
                            checked={checkedIds.has(a.actionId)}
                            onSelect={() => onSelect(a)}
                            onToggleCheck={() => onToggleCheck(a.actionId)}
                        />
                    ))}
                </div>
            )}

            {/* Counts the FILTERED list. Against actions.length this read
                "Showing 3 of 100" with a Load more button that added nothing. */}
            {visible.length > 0 && (
                <div style={{ padding: '12px 4px 2px', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="cc-tiny">
                        Showing {shown.length} of {visible.length}
                        {statusFilter !== 'all' && ` ${activeLabel.toLowerCase()}`}
                    </span>
                    <span style={{ flex: 1 }} />
                    {limit < visible.length && (
                        <button type="button" className="cc-btn cc-btn-ghost cc-btn-sm" onClick={() => setLimit((l) => l + PAGE)}>
                            Load more<ChevronDown style={{ width: 11, height: 11 }} />
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default DigestQueue;
